/** `/notifications/{id}/anatomy/` + `/notifications/template_anatomy/` — the
 *  read-only structure viewer's two endpoints.
 *
 *  Hand-authored wrapper (console convention: feature modules never depend on
 *  generated types).
 *
 *  Two things about this contract are easy to get wrong:
 *
 *  1. The target-format override is `target_format`, **not** `format`. `format`
 *     is DRF's reserved content-negotiation parameter — an unknown value there
 *     fails negotiation with a 404 before the view ever runs.
 *  2. `feature_gates` are the flag ROWS that exist, never a verdict. A verdict
 *     needs a rendering context, which this endpoint deliberately has not got.
 *     Rendering them as pass/fail would be a lie.
 */
import { apiGet, apiPost } from './client';
import type { EvaluateContext } from './notificationTestRender';

export type AnatomyKind =
  | 'root'
  | 'text'
  | 'var'
  | 'if'
  | 'branch'
  | 'for'
  | 'ifruleset'
  | 'container'
  | 'tag'
  | 'truncation';

export interface AnatomyBadge {
  code: string;
  label: string;
  /** Block codename, for `block_ref` / `unknown_block`. */
  ref?: string;
}

/** A parsed `{% if %}` condition: Django's own operator tree. */
export type AnatomyCondition =
  | { op: string; args: AnatomyCondition[] }
  | { var: string; filters?: { name: string; args: unknown[] }[] }
  | { lit: unknown };

export interface AnatomyNode {
  /** Stable dotted path — also the expand-state key in the URL. */
  id: string;
  kind: AnatomyKind;
  label: string;
  line?: number | null;
  /** Node count beneath this one. The collapse hint. */
  descendants: number;
  badges: AnatomyBadge[];
  children: AnatomyNode[];
  /** text nodes only — the body is never shipped. */
  preview?: string;
  length?: number;
  /** text nodes only — a whitespace-only run. */
  blank?: boolean;
  /** branch nodes only. */
  condition_raw?: string;
  condition?: AnatomyCondition | null;
  truncated?: boolean;
  /** Present only on a traced anatomy. See `AnatomyVerdict`. */
  verdict?: AnatomyVerdict;
}

/** What one node did, for one context.
 *
 *  `taken` / `skipped` are MEASURED — the condition was evaluated against a
 *  test-render context. `not_reached` is DERIVED from the ancestors. `in_loop`
 *  and `error` are refusals to answer, and the first is the one to read
 *  carefully: it means the operands are per-iteration and were deliberately not
 *  evaluated — NOT that the node did not fire. `empty` / `value` apply to
 *  `{{ ... }}` nodes, and `empty` is usually the answer to "why is this section
 *  blank". */
export interface AnatomyVerdict {
  state: 'taken' | 'skipped' | 'not_reached' | 'in_loop' | 'error' | 'empty' | 'value';
  detail: string | null;
  /** state=value — the rendered length. */
  length?: number;
}

export interface AnatomyStats {
  nodes: number;
  max_depth: number;
  if_count: number;
  branch_count: number;
  for_count: number;
  tag_count: number;
  text_length: number;
  max_switch: number;
  tour_id_conditions: number;
  language_conditions: number;
  block_refs: string[];
  unknown_block_refs: string[];
}

/** Where a line in a template tree can be edited.
 *
 *  `null` means the source has no console editor — a shared `Standard_Template`
 *  block — so the viewer must offer no link rather than a broken one. */
export type AnatomyEditTarget =
  | { kind: 'delivery_method'; dm_id: number; field: string }
  | { kind: 'block'; block_id: number };

export interface TemplateAnatomy {
  parsed: boolean;
  /** Why no tree was built. */
  reason: 'jinja2' | 'too_large' | null;
  language: 'django' | 'jinja2';
  bytes: number;
  root: AnatomyNode | null;
  errors: { message: string; line: number | null }[];
  stats: AnatomyStats | null;
  truncated: boolean;
  edit_target: AnatomyEditTarget | null;
}

export interface AnatomyGateLevel {
  level: 'system' | 'ota' | 'tenant' | 'tour' | 'notification' | 'tag';
  label: string;
  row_count: number;
  /** The `tag` level: `enabled` means "condition enforced", not "feature on". */
  inverted: boolean;
  rows_truncated?: boolean;
  rows: { id: number; enabled: boolean; comment: string; tag_match_mode: string | null }[];
}

export type AnatomyChannel = 'email' | 'sms' | 'whatsapp' | 'chat' | 'airbnb';
export type AnatomyFormat = 'html' | 'markdown' | 'plaintext';

export interface AnatomyDeliveryMethod {
  id: number;
  method: number;
  method_label: string;
  channel: AnatomyChannel | null;
  enabled: boolean;
  sequence: number;
  theme: number | null;
  rendering_type: number | null;
}

export interface AnatomyLayout {
  custom: boolean;
  rendering_type: number;
  standard_template: {
    id: number;
    name: string;
    version: number;
    template_language: string;
  } | null;
  blocks: {
    id: number;
    codename: string;
    name: string;
    sequence: number;
    anatomy: TemplateAnatomy;
  }[];
  /** Set only when `custom` — the delivery method's own body. */
  anatomy: TemplateAnatomy | null;
}

/** Kept in step with `NotificationBlockType` (the block catalog's own union). */
export type AnatomyBlockType =
  | 'template'
  | 'ruleset'
  | 'dotty'
  | 'jsonpath'
  | 'function'
  | 'constant'
  | 'rule'
  | 'tag';

export interface AnatomyBlock {
  /** Effective name in the `block.*` namespace (the ref alias, if any). */
  alias: string;
  block_id: number;
  codename: string;
  name: string;
  type: AnatomyBlockType;
  feature: string | null;
  enabled: boolean;
  error_mode: 'soft' | 'hard';
  default_value: string | null;
  template_language: string | null;
  /** Best-effort provenance: a direct ref, or a list / default list. */
  source: 'direct' | 'list';
  shadowed: boolean;
  definition_length: number;
  /** Non-template types only — the definition IS the value spec. */
  definition_preview: string | null;
  anatomy: TemplateAnatomy | null;
}

export interface AnatomyAdvancedRule {
  id: number;
  condition: number;
  condition_label: string;
  condition_any_all: number;
  condition_any_all_label: string;
  send_after_min: number | null;
  triggers: number[];
  dependents: number[];
}

export interface NotificationAnatomy {
  notification: {
    id: number;
    name: string;
    event: string;
    enabled: boolean;
    tour_operator: { id: number; code: string; name: string } | null;
    language_policy: { id: number; codename: string } | null;
    disable_default_blocks: boolean;
  };
  channel: AnatomyChannel | null;
  format: AnatomyFormat;
  delivery_methods: AnatomyDeliveryMethod[];
  layout: AnatomyLayout | null;
  /** email only — the subject line is a template too. */
  subject: TemplateAnatomy | null;
  blocks: AnatomyBlock[];
  /** feature codename → the six levels, broadest first. */
  feature_gates: Record<string, AnatomyGateLevel[]>;
  advanced_rules: {
    ordering: number;
    ordering_label: string;
    /** Rows survive an ordering change — false means they never run. */
    active: boolean;
    rules: AnatomyAdvancedRule[];
  };
  stats: AnatomyStats;
  truncated: boolean;
  notes: string[];
  /** Present only on a traced anatomy. */
  trace?: {
    available: boolean;
    /** available=false — why nothing was traced. */
    reason?: string;
    evaluations?: number;
    truncated?: boolean;
    traced?: string[];
    notes?: string[];
  };
}

export function getNotificationAnatomy(
  id: number,
  params: { channel?: AnatomyChannel; targetFormat?: AnatomyFormat } = {},
): Promise<NotificationAnatomy> {
  const qs = new URLSearchParams();
  if (params.channel) qs.set('channel', params.channel);
  // NOT `format` — see the module docstring.
  if (params.targetFormat) qs.set('target_format', params.targetFormat);
  const suffix = qs.toString();
  return apiGet(`/notifications/${id}/anatomy/${suffix ? `?${suffix}` : ''}`);
}

/** The same tree, plus per-node verdicts for ONE context.
 *
 *  Nothing is sent and nothing persists: the template engine is not
 *  instrumented — the backend evaluates each condition against the context a
 *  test render would use. `trace.notes` states what it refused to answer. */
export function traceNotificationAnatomy(
  id: number,
  body: {
    context: EvaluateContext;
    channel?: AnatomyChannel;
    target_format?: AnatomyFormat;
  },
): Promise<NotificationAnatomy> {
  return apiPost(`/notifications/${id}/anatomy_trace/`, body);
}

export function getTemplateAnatomy(body: {
  source: string;
  language?: 'django' | 'jinja2';
  tour_operator?: number | null;
}): Promise<TemplateAnatomy> {
  return apiPost('/notifications/template_anatomy/', body);
}
